/**
 * RelationshipsPanel — composes RelationshipEgoGraph + RelatedObjectsChips
 * into the single component every detail view drops in to show an object's
 * cross-kind relationships. Falls back to EmptyState when relationshipsFor()
 * returns no edges, per the project's "no blank panes" convention.
 *
 * @see docs/superpowers/specs/2026-08-10-atlas-feature-flag-relationships-design.md Part 3
 */
import type { JSX } from 'react';
import { EmptyState } from './EmptyState.js';
import { RelatedObjectsChips } from './RelatedObjectsChips.js';
import { RelationshipEgoGraph } from '../views/RelationshipEgoGraph.js';
import { relationshipsFor } from '../state/selectors.js';
import type { ObjectKind } from '../state/objectKinds.js';
import type { AtlasManifest } from '@atlas-manifest/types';

export interface RelationshipsPanelProps {
  manifest: AtlasManifest;
  kind: ObjectKind;
  id: string;
  onNavigate: (kind: ObjectKind, id: string) => void;
}

export function RelationshipsPanel({ manifest, kind, id, onNavigate }: RelationshipsPanelProps): JSX.Element {
  const edges = relationshipsFor(manifest, kind, id);
  return (
    <section className="relationships-panel" aria-label="Relationships">
      <h2 className="relationships-panel-title">Relationships</h2>
      {edges.length === 0 ? (
        <EmptyState
          icon="hub"
          title="No related objects"
          detail="This object has no linked entities, surfaces, features, or flags yet."
        />
      ) : (
        <>
          <RelationshipEgoGraph manifest={manifest} kind={kind} id={id} onNavigate={onNavigate} />
          <RelatedObjectsChips edges={edges} onNavigate={onNavigate} />
        </>
      )}
    </section>
  );
}
